import { IRV } from '@curbnturf/entities';
import { RvType, RV_TYPES } from './listings/mock-listings.data';

/**
 * The rig a guest described in the URL, as opposed to the one they saved in
 * their garage (`my-rv.util.ts`). Two pages need to agree on this and, until
 * now, neither shared a line of code with the other: `/search` hand-rolled its
 * parsing inline and the listing page did not parse at all, which is why a rig
 * typed in the hero never survived the click into a listing (card F9).
 *
 * Deliberately returns the `IRV` shape — the same one `readMyRv()` returns —
 * because both `myRvFit` getters and `listing-card`'s `rvFit` already consume
 * exactly `{ type, length }` off it. A parsed rig is therefore a drop-in
 * fallback wherever a saved rig is read, with no call-site branching.
 */

/** Params as Angular hands them over: `ActivatedRoute.queryParams`, or a plain map. */
type ParamBag = Record<string, unknown> | { get(name: string): string | null };

function read(params: ParamBag, name: string): string | null {
  if (typeof (params as { get?: unknown }).get === 'function') {
    return (params as { get(n: string): string | null }).get(name);
  }
  const v = (params as Record<string, unknown>)[name];
  if (v == null) return null;
  return Array.isArray(v) ? String(v[0] ?? '') : String(v);
}

/**
 * Two vocabularies reach `/search` and they are not interchangeable:
 *
 * - `rt/rl/rh/rw` — the canonical short forms the search page reads AND writes,
 *   so they round-trip. `rt` carries the slug id (`classA`).
 * - `rigType/rigLength` — long forms the home hero emits on its way to
 *   `/search` (`home-hero.component.ts`). `rigType` carries the user-facing
 *   LABEL (`"Class A"`), not the id.
 *
 * Accepting both here is what lets a listing opened straight from the hero
 * behave the same as one opened after the search page has rewritten the URL.
 */
function parseType(params: ParamBag): RvType | undefined {
  const short = read(params, 'rt');
  if (short && RV_TYPES.some((t) => t.id === short)) return short as RvType;

  const long = read(params, 'rigType');
  if (!long) return undefined;
  // The hero sends a label; tolerate an id here too, since nothing stops a
  // hand-edited or shared URL carrying one.
  const match =
    RV_TYPES.find((t) => t.id === long) ??
    RV_TYPES.find((t) => t.label.toLowerCase() === long.toLowerCase());
  return match?.id;
}

function parseDimension(params: ParamBag, shortName: string, longName?: string): number | undefined {
  const raw = read(params, shortName) ?? (longName ? read(params, longName) : null);
  if (raw == null || raw === '') return undefined;
  const n = Number(raw);
  // Guard against `?rl=abc` and `?rl=-4`: a nonsense value must read as "no rig
  // stated" rather than produce a fit verdict against NaN.
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * Builds a rig from URL params, or `null` when the URL says nothing usable.
 *
 * Note there is no long form for height/width — the hero never collects them,
 * so a guest arriving straight from the hero gets type and length only. That is
 * enough for the fit check, which compares length against the site's maximum.
 */
export function parseRigParams(params: ParamBag): Partial<IRV> | null {
  const type = parseType(params);
  const length = parseDimension(params, 'rl', 'rigLength');
  const height = parseDimension(params, 'rh');
  const width = parseDimension(params, 'rw');

  if (!type && !length && !height && !width) return null;
  return { type, length, height, width };
}

/**
 * The inverse, in the canonical short forms only — never the hero's long forms,
 * which exist solely as an inbound compatibility shim. Values that are absent
 * are omitted rather than emitted empty, so a listing link for a guest who
 * named no rig stays clean.
 */
export function serializeRigParams(rig: Partial<IRV> | null | undefined): Record<string, string> {
  if (!rig) return {};
  const out: Record<string, string> = {};
  if (rig.type) out['rt'] = String(rig.type);
  if (typeof rig.length === 'number' && rig.length > 0) out['rl'] = String(rig.length);
  if (typeof rig.height === 'number' && rig.height > 0) out['rh'] = String(rig.height);
  if (typeof rig.width === 'number' && rig.width > 0) out['rw'] = String(rig.width);
  return out;
}
